import type { BatchTutorsResponse, GradeOption, GroupOption, PaginationMeta } from 'api/arcMosaico/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  batchCreateTutors,
  deleteTutor,
  listGrades,
  listGroups,
  listTutors,
  updateTutor,
} from '../service/manageFacultyService'
import { createGroupQuick } from '../../TeacherDashboard/service/studentRequestsService'
import type { ManagedFaculty } from '../types'

const ITEMS_PER_PAGE = 9

interface Filters {
  search: string
  page: number
}

export function useManageFaculty() {
  const [faculty, setFaculty] = useState<ManagedFaculty[]>([])
  const [grades, setGrades] = useState<GradeOption[]>([])
  const [groups, setGroups] = useState<GroupOption[]>([])
  const courseIdRef = useRef<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<PaginationMeta | null>(null)
  const [filters, setFilters] = useState<Filters>({ search: '', page: 1 })

  const mountedRef = useRef(true)
  const fetchAbortRef = useRef<AbortController | null>(null)

  // -- Fetch tutors --
  const fetchFaculty = useCallback(
    async (f?: Filters) => {
      const currentFilters = f || filters
      fetchAbortRef.current?.abort()
      const abort = new AbortController()
      fetchAbortRef.current = abort

      setLoading(true)
      setError(null)

      try {
        const result = await listTutors({
          page: currentFilters.page,
          limit: ITEMS_PER_PAGE,
          search: currentFilters.search || undefined,
        })

        if (!mountedRef.current || abort.signal.aborted) return

        setFaculty(result.tutors)
        setPagination(result.pagination)
      } catch (err) {
        if (!mountedRef.current || abort.signal.aborted) return
        setError(err instanceof Error ? err.message : 'Failed to load')
      } finally {
        if (mountedRef.current && !abort.signal.aborted) {
          setLoading(false)
        }
      }
    },
    [filters],
  )

  // -- Fetch grades & groups --
  const fetchOptions = useCallback(async () => {
    try {
      const [g, { groups: gr, courseId }] = await Promise.all([listGrades(), listGroups()])
      if (!mountedRef.current) return
      setGrades(g)
      setGroups(gr)
      courseIdRef.current = courseId
    } catch {
      // Non-blocking — faculty list still works
    }
  }, [])

  // -- Initial load --
  useEffect(() => {
    fetchFaculty(filters)
    fetchOptions()
  }, [])

  // -- Refetch on filter change (skip initial) --
  const initialRef = useRef(true)
  useEffect(() => {
    if (initialRef.current) {
      initialRef.current = false
      return
    }
    fetchFaculty(filters)
  }, [filters.page, filters.search])

  // -- Cleanup --
  useEffect(() => {
    return () => {
      mountedRef.current = false
      fetchAbortRef.current?.abort()
    }
  }, [])

  // -- Filter setters --
  const setSearch = useCallback((search: string) => {
    setFilters(prev => ({ ...prev, search, page: 1 }))
  }, [])

  const setPage = useCallback((page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }, [])

  // -- Mutations --
  const addFaculty = useCallback(
    async (
      rows: Array<{
        firstName: string
        lastName: string
        email: string
        gradeIds: string[]
        groupIds: string[]
      }>,
    ): Promise<BatchTutorsResponse> => {
      const result = await batchCreateTutors(
        rows.map(r => ({
          firstName: r.firstName,
          lastName: r.lastName,
          email: r.email,
          gradeIds: r.gradeIds,
          groupSchoolTermIds: r.groupIds,
        })),
      )
      if (mountedRef.current) await fetchFaculty()
      return result
    },
    [fetchFaculty],
  )

  const editFaculty = useCallback(
    async (
      id: string,
      data: {
        firstName: string
        lastName: string
        role: string
        gradeIds: string[]
        groupIds: string[]
      },
    ) => {
      await updateTutor(id, {
        firstName: data.firstName,
        lastName: data.lastName,
        educatorRole: data.role,
        gradeIds: data.gradeIds,
        groupSchoolTermIds: data.groupIds,
      })
      if (mountedRef.current) await fetchFaculty()
    },
    [fetchFaculty],
  )

  const removeFaculty = useCallback(
    async (id: string) => {
      await deleteTutor(id)
      if (mountedRef.current) await fetchFaculty()
    },
    [fetchFaculty],
  )

  const addGroup = useCallback(async (name: string): Promise<GroupOption> => {
    const newGroup = await (createGroupQuick as (name: string, courseId?: string | null) => Promise<GroupOption>)(name, courseIdRef.current)
    if (mountedRef.current) setGroups(prev => [...prev, newGroup])
    return newGroup
  }, [])

  const totalPages = pagination?.pages || 1

  return {
    faculty,
    grades,
    groups,
    loading,
    error,
    totalPages,
    filters,
    setSearch,
    setPage,
    addFaculty,
    editFaculty,
    removeFaculty,
    addGroup,
  }
}
